/**
 * Skills Reference Page Utilities
 *
 * Pure utility functions for organizing skill configurations
 * into a section → category → skills hierarchy, deriving coach
 * groups from rider associations, and selecting default admin groups.
 *
 * Requirements: 3.1, 3.4, 4.1, 5.1, 5.2, 5.3, 5.4
 */

import type { SkillConfig } from '../types/skills';
import type { Group } from '../api/rest-client';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface SkillEntry {
  skill_id: string;
  skill_name: string;
  description: string;
}

export interface CategoryGroup {
  category: string;
  skills: SkillEntry[];
}

export interface SectionGroup {
  section: string;
  categories: CategoryGroup[];
}

// ---------------------------------------------------------------------------
// organizeSkillsBySection
// ---------------------------------------------------------------------------

/**
 * Groups skills by section then category, sorting everything alphabetically.
 *
 * - Sections sorted alphabetically
 * - Categories sorted alphabetically within each section
 * - Skills sorted alphabetically by skill_name within each category
 * - Returns [] for empty input
 */
export function organizeSkillsBySection(skills: SkillConfig[]): SectionGroup[] {
  if (skills.length === 0) return [];

  // Build a map: section → category → skills[]
  const sectionMap = new Map<string, Map<string, SkillEntry[]>>();

  for (const skill of skills) {
    if (!sectionMap.has(skill.section)) {
      sectionMap.set(skill.section, new Map());
    }
    const categoryMap = sectionMap.get(skill.section)!;

    if (!categoryMap.has(skill.category)) {
      categoryMap.set(skill.category, []);
    }
    categoryMap.get(skill.category)!.push({
      skill_id: skill.skill_id,
      skill_name: skill.skill_name,
      description: skill.description,
    });
  }

  // Convert maps to sorted arrays
  const sections: SectionGroup[] = Array.from(sectionMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([section, categoryMap]) => ({
      section,
      categories: Array.from(categoryMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, skills]) => ({
          category,
          skills: [...skills].sort((a, b) => a.skill_name.localeCompare(b.skill_name)),
        })),
    }));

  return sections;
}

// ---------------------------------------------------------------------------
// deriveCoachGroup
// ---------------------------------------------------------------------------

/**
 * Derives the coach's group from a list of rider associations.
 *
 * Priority chain:
 * 1. Rider with `role === 'Self'` → use that rider's `group`
 * 2. First rider with `type === 'Coach'` → use that rider's `group`
 * 3. `null` (no group found)
 *
 * Requirements: 3.1, 3.4
 */
export function deriveCoachGroup(
  riders: Array<{ role?: string; type?: string; group?: string }>,
): string | null {
  if (!riders || riders.length === 0) return null;

  const selfRider = riders.find((r) => r.role === 'Self');
  if (selfRider?.group) return selfRider.group;

  const coachRider = riders.find((r) => r.type === 'Coach');
  if (coachRider?.group) return coachRider.group;

  return null;
}

// ---------------------------------------------------------------------------
// getDefaultAdminGroup
// ---------------------------------------------------------------------------

/**
 * Returns the group with the lowest `order` value.
 *
 * - Groups without an `order` are treated as `Infinity` (sorted last).
 * - Returns `null` for an empty array.
 *
 * Requirements: 4.1
 */
export function getDefaultAdminGroup(groups: Group[]): Group | null {
  if (!groups || groups.length === 0) return null;

  return groups.reduce<Group>((lowest, group) => {
    const lowestOrder = lowest.order ?? Infinity;
    const currentOrder = group.order ?? Infinity;
    return currentOrder < lowestOrder ? group : lowest;
  }, groups[0]);
}
